// domain/services/serviceCodeDraft.js

import { isUnitBasis } from '../constants/unitBasis';
import { CODE_STRING_REJECTION, codeStringRejection } from './codeString';
import { toAsOfDate } from './effectiveDating';

/**
 * What a code needs before it can exist, as codes a screen turns into field messages.
 *
 * THE SAME SHAPE `validateCoverage` USES, and deliberately so: a pure function returning error
 * CODES, with the wording owned by whichever screen is asking. That is what lets the mint form hang
 * ordinary `:rules` off individual fields — the form collects, the domain decides — instead of the
 * form re-deriving the rules and the two drifting apart.
 *
 * ONE SOURCE FOR THE FORM AND THE USE CASE. `mintServiceCode` refuses the same conditions on its way
 * to the adapter, because a use case cannot trust that a caller ran the form; both read this, so a
 * rule can only ever be added in one place.
 *
 * IT DOES NOT KNOW ABOUT DUPLICATES OR SHADOWS. Those are questions about the rest of the clinic's
 * codes rather than about this draft, they need the catalog to answer, and one of them is advisory.
 * They live in `serviceCodeGovernance`, where the screen can treat them as findings rather than as
 * a field being wrong.
 *
 * @domainSupport The validation vocabulary for an unsaved org-authored code.
 * @typedef {'codeRequired' | 'codeTooLong' | 'codeUnstorable' | 'descriptorRequired'
 *   | 'unitBasisRequired' | 'validFromRequired' | 'validFromUnreadable'} ServiceCodeDraftError
 */
export const SERVICE_CODE_DRAFT_ERRORS = {
  CODE_REQUIRED: 'codeRequired',
  CODE_TOO_LONG: 'codeTooLong',
  CODE_UNSTORABLE: 'codeUnstorable',
  DESCRIPTOR_REQUIRED: 'descriptorRequired',
  UNIT_BASIS_REQUIRED: 'unitBasisRequired',
  VALID_FROM_REQUIRED: 'validFromRequired',
  VALID_FROM_UNREADABLE: 'validFromUnreadable',
};

/** `codeStringRejection`'s vocabulary is storage's; this is the domain's. Mapped, never merged. */
const CODE_REJECTION_ERROR = {
  [CODE_STRING_REJECTION.EMPTY]: SERVICE_CODE_DRAFT_ERRORS.CODE_REQUIRED,
  [CODE_STRING_REJECTION.TOO_LONG]: SERVICE_CODE_DRAFT_ERRORS.CODE_TOO_LONG,
  [CODE_STRING_REJECTION.UNSTORABLE_CHARACTERS]: SERVICE_CODE_DRAFT_ERRORS.CODE_UNSTORABLE,
};

/**
 * Everything wrong with a draft, as error codes. Empty means it can be written.
 *
 * REQUIRED AND UNREADABLE ARE SEPARATE CODES for the start date, because they are different mistakes
 * with different fixes: nobody has chosen one yet, versus something handed over a value that is not
 * a calendar date. The second cannot happen through the current picker and is the tripwire for a
 * date input that speaks another type being swapped in — the same guard `validateCoverage` keeps.
 *
 * @param {{ code: *, descriptor: *, unitBasis: *, validFrom: * }} draft
 * @returns {string[]} SERVICE_CODE_DRAFT_ERRORS members
 */
// eslint-disable-next-line import/prefer-default-export
export function validateServiceCodeDraft({
  code, descriptor, unitBasis, validFrom,
} = {}) {
  const errors = [];
  const codeRejection = codeStringRejection(code);

  if (codeRejection !== null) {
    errors.push(CODE_REJECTION_ERROR[codeRejection] ?? SERVICE_CODE_DRAFT_ERRORS.CODE_REQUIRED);
  }
  if (typeof descriptor !== 'string' || descriptor.trim().length === 0) {
    errors.push(SERVICE_CODE_DRAFT_ERRORS.DESCRIPTOR_REQUIRED);
  }
  // REFUSED RATHER THAN DEFAULTED, on the rule that a field naming a mechanism never takes one. The
  // basis says what one unit IS, so guessing prices the code against a span nobody chose, and
  // per-15-min against per-hour is a fourfold difference on every claim it produces.
  if (!isUnitBasis(unitBasis)) {
    errors.push(SERVICE_CODE_DRAFT_ERRORS.UNIT_BASIS_REQUIRED);
  }
  if (validFrom === null || validFrom === undefined || validFrom === '') {
    errors.push(SERVICE_CODE_DRAFT_ERRORS.VALID_FROM_REQUIRED);
  } else if (toAsOfDate(validFrom) === null) {
    errors.push(SERVICE_CODE_DRAFT_ERRORS.VALID_FROM_UNREADABLE);
  }

  return errors;
} // validateServiceCodeDraft()
